import { useRef } from 'react'

import { Actions, Entities } from '@space-lib/datahub'
import useClickOutside from '@space-shell/common/hooks/useClickOutside'
import useComponentAttributeObserver from '@space-shell/common/hooks/useComponentAttributeObserver'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { Context } from '@space-shell/common/services/notificationCenter'
import { useTranslation } from 'react-i18next'

import {
  ActivitiesNotificationsList,
  NotificationIcon,
  NotificationNavTab,
  ProductNotificationsList
} from './components'
import { useActivitiesNotificationCount, useProductsNotificationCount } from './hooks'

const NotificationCenter = () => {
  const hotDropdownRef = useRef<JSX.IntrinsicElements['hot-dropdown']>(null)
  const { t } = useTranslation()
  const isOpenNotificationMenu = useComponentAttributeObserver({
    element: hotDropdownRef.current,
    attribute: 'active'
  })

  const { sendDataHubEvent } = useDataHubEvents()

  const productsNotificationCount = useProductsNotificationCount()
  const activitiesNotificationCount = useActivitiesNotificationCount()

  const hasUnreadProductsNotification =
    productsNotificationCount.isSuccess && productsNotificationCount.data.unread > 0

  const hasUnreadActivitiesNotification =
    activitiesNotificationCount.isSuccess && activitiesNotificationCount.data.unread > 0

  const hasUnreadNotification = hasUnreadProductsNotification || hasUnreadActivitiesNotification

  const handleClickOpenNotificationMenu = () => {
    if (!isOpenNotificationMenu) {
      sendDataHubEvent({
        group: Entities.NOTIFICATIONS_CENTER,
        name: Actions.CLICK_NOTIFICATIONS_CENTER_OPEN_MENU
      })
    }
  }

  const handleClickOutside = () => {
    if (isOpenNotificationMenu) {
      hotDropdownRef.current?.closeMenu()
    }
  }

  useClickOutside(hotDropdownRef, handleClickOutside)

  return (
    <hot-dropdown ref={hotDropdownRef} slot='actions-end' class='hot-dropdocked'>
      <hot-menu-item
        data-test='open-notification-menu-button'
        slot='button'
        role='button'
        class='_cursor-pointer'
        onClick={handleClickOpenNotificationMenu}>
        <NotificationIcon showBadge={hasUnreadNotification} />
      </hot-menu-item>

      <div slot='menu'>
        <hot-dropdown-menu-header class='_p-0'>
          <h2 className='_text-gray-800 _text-2 _p-4 _pb-5 _mb-0'>
            <span>{t('notifications.heading')}</span>
          </h2>
        </hot-dropdown-menu-header>

        <div>
          {isOpenNotificationMenu && (
            <NotificationNavTab
              notificationsTab={[
                {
                  id: Context.ACTIVITIES,
                  label: t('notifications.context.activities'),
                  component: <ActivitiesNotificationsList />,
                  hasUnreadNotification: hasUnreadActivitiesNotification
                },
                {
                  id: Context.PRODUCTS,
                  label: t('notifications.context.products'),
                  component: <ProductNotificationsList />,
                  hasUnreadNotification: hasUnreadProductsNotification
                }
              ]}
              defaultNotificationTabId={Context.ACTIVITIES}
            />
          )}
        </div>
      </div>
    </hot-dropdown>
  )
}

export default NotificationCenter
